// BookingFilterBar.jsx
import React, { useState, useEffect } from "react";
import { useQuery } from "@apollo/client";
import { EMPLOYEE_LIST } from "./graphql";
import moment from "moment";
import Select from "react-select";

const previousBookingsStatusOptions = [
  "Pending",
  "Confirmed",
  "Headed",
  "Reached",
  "Started",
  "Scanned",
  "OnHold",
  "Completed",
  "Cancelled",
  "Declined",
];

const upcomingBookingStatusOptions = [
  "Pending",
  "Confirmed",
  "Headed",
  "Reached",
  "Started",
  "Scanned",
  "OnHold",
];

const previousBookingDatePresets = [
  { label: "Last 7 Days", start: moment().subtract(7, "days"), end: moment() },
  { label: "Last Month", start: moment().subtract(1, "month"), end: moment() },
  { label: "Last Year", start: moment().subtract(1, "year"), end: moment() },
  { label: "Overall", start: null, end: null },
  { label: "Custom", start: null, end: null },
];

const upcomingBookingDatePresets = [
  { label: "Today", start: moment(), end: moment() },
  {
    label: "Tomorrow",
    start: moment().add(1, "days"),
    end: moment().add(1, "days"),
  },
  { label: "Overall", start: moment(), end: moment().add(10, "days") },
  { label: "Custom", start: null, end: null },
];

const verificationStatusOptions = [
  { label: "All", value: null },
  { label: "PENDING", value: "PENDING" },
  {
    label: "PAYMENT_VERIFICATION_PENDING",
    value: "PAYMENT_VERIFICATION_PENDING",
  },
  { label: "SCAN_VERIFICATION_PENDING", value: "SCAN_VERIFICATION_PENDING" },
  { label: "VERIFIED", value: "VERIFIED" },
  { label: "PAYMENT VERIFIED", value: "PAYMENT_VERIFIED" },
];

const BookingFilterBar = ({
  onFilterChange,
  isPreviousBooking,
  currentFilter = {},
  issuedReportedBookings = 0,
  bookingsWithRemark = 0,
  unacknowledgedCount = 0,
  dummyEmployeeCount = 0,
  blockedUserCount = 0,
  noShowAwaitingCount = 0,
}) => {
  const { data } = useQuery(EMPLOYEE_LIST, {
    variables: {
      data: {
        bookableOrEmployed: true,
      },
    },
  });

  const datePresets = isPreviousBooking
    ? previousBookingDatePresets
    : upcomingBookingDatePresets;

  const defaultStatuses = isPreviousBooking
    ? previousBookingsStatusOptions
    : upcomingBookingStatusOptions;

  // ---------- INITIAL VALUES (used only on first mount) ----------
  const initialEmployeeIds = Array.isArray(currentFilter.employeeIds)
    ? currentFilter.employeeIds
    : [];

  const initialIssueType = currentFilter.issueType ?? null;

  const initialHasRemark = currentFilter.hasRemark ?? false;

  const initialStatuses = Array.isArray(currentFilter.status)
    ? currentFilter.status
    : typeof currentFilter.status === "undefined"
    ? defaultStatuses
    : []; // null -> no statuses

  const initialSelectedPreset =
    (currentFilter.selectedDatePreset &&
      datePresets.find((p) => p.label === currentFilter.selectedDatePreset)) ||
    datePresets[0];

  const initialCustomStart = currentFilter.customStart ?? "";
  const initialCustomEnd = currentFilter.customEnd ?? "";
  const initialVerificationStatus = currentFilter.verificationStatus ?? null;

  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState(initialEmployeeIds);
  const [selectedStatuses, setSelectedStatuses] = useState(initialStatuses);
  const [selectedDatePreset, setSelectedDatePreset] = useState(
    initialSelectedPreset
  );
  const [customStart, setCustomStart] = useState(initialCustomStart);
  const [customEnd, setCustomEnd] = useState(initialCustomEnd);
  const [selectedVerificationStatus, setSelectedVerificationStatus] = useState(
    initialVerificationStatus
  );

  const [selectedIssueType, setSelectedIssueType] = useState(initialIssueType);
  const [selectedHasRemark, setSelectedHasRemark] = useState(initialHasRemark);
  const [showUnacknowledged, setShowUnacknowledged] = useState(false);
  const [showDummyEmployee, setShowDummyEmployee] = useState(
    currentFilter.isDummyEmployee === true
  );
  const [showBlockedUser, setShowBlockedUser] = useState(
    currentFilter.isFromBlockedUser === true
  );
  const [showNoShowAwaiting, setShowNoShowAwaiting] = useState(
    currentFilter.noShowHoldStatus === "AWAITING_RESPONSE"
  );
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // 🔄 When switching between Upcoming / Previous, reset to defaults of that mode
  useEffect(() => {
    const preset0 = datePresets[0];

    setSelectedEmployeeIds([]);
    setSelectedStatuses(defaultStatuses);
    setSelectedDatePreset(preset0);
    setCustomStart("");
    setCustomEnd("");
    setSelectedVerificationStatus(null);
    setSelectedHasRemark(false);
    setShowUnacknowledged(false);
    setShowDummyEmployee(false);
    setShowBlockedUser(false);
    setShowNoShowAwaiting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPreviousBooking]); // we intentionally ignore datePresets/defaultStatuses warnings

  // ---------- PROPAGATE FILTER TO PARENT ----------
  useEffect(() => {
    const isCustom = selectedDatePreset?.label === "Custom";

    // ⛔ For "Custom": don't propagate until BOTH dates are selected
    if (isCustom && (!customStart || !customEnd)) {
      return;
    }

    const startDate =
      isCustom && customStart
        ? customStart
        : selectedDatePreset?.start?.format?.("YYYY-MM-DD") || null;
    const endDate =
      isCustom && customEnd
        ? customEnd
        : selectedDatePreset?.end?.format?.("YYYY-MM-DD") || null;

    onFilterChange({
      employeeIds: selectedEmployeeIds.length ? selectedEmployeeIds : null,
      status:
        selectedStatuses && selectedStatuses.length ? selectedStatuses : null,
      queryStartDate: startDate,
      queryEndDate: endDate,
      verificationStatus: selectedVerificationStatus ?? null,
      isCustomDateRange: isCustom,
      selectedDatePreset: selectedDatePreset?.label || null,
      customStart: isCustom ? customStart : null,
      customEnd: isCustom ? customEnd : null,
      issueType: selectedIssueType,
      hasRemark: selectedHasRemark || null,
      isAdminAcknowledged: showUnacknowledged ? false : undefined,
      isDummyEmployee: showDummyEmployee ? true : undefined,
      isFromBlockedUser: showBlockedUser ? true : undefined,
      noShowHoldStatus: showNoShowAwaiting ? "AWAITING_RESPONSE" : undefined,
      bookingId: currentFilter.bookingId || null,
      contactNumber: currentFilter.contactNumber || null,
    });
  }, [
    selectedEmployeeIds,
    selectedStatuses,
    selectedDatePreset,
    customStart,
    customEnd,
    showNoShowAwaiting,
    selectedVerificationStatus,
    selectedIssueType,
    selectedHasRemark,
    showUnacknowledged,
    showDummyEmployee,
    showBlockedUser,
    currentFilter.bookingId,
    currentFilter.contactNumber,
    onFilterChange,
  ]);

  // ---------- HANDLERS ----------
  const handleStatusToggle = (status) => {
    setSelectedStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );
  };

  const handleToggleIssueType = () => {
    setSelectedIssueType((prev) => (prev === "REPORTED" ? null : "REPORTED"));
  }

  const handleToggleHasRemark = () => {
    setSelectedHasRemark((prev) => !prev);
  }

  const handleClearAllStatuses = () => {
    setSelectedStatuses([]);
  };

  const handleResetFilters = () => {
    const preset0 = datePresets[0];
    setSelectedEmployeeIds([]);
    setSelectedStatuses(defaultStatuses);
    setSelectedVerificationStatus(null);
    setSelectedDatePreset(preset0);
    setCustomStart("");
    setCustomEnd("");
    setSelectedHasRemark(false);
    setShowUnacknowledged(false);
    setShowDummyEmployee(false);
    setShowBlockedUser(false);
    setShowNoShowAwaiting(false);
  };


  // ---------- RENDER ----------
  const activeFilterCount =
    selectedEmployeeIds.length +
    (selectedVerificationStatus ? 1 : 0) +
    (selectedIssueType ? 1 : 0) +
    (selectedHasRemark ? 1 : 0) +
    (showUnacknowledged ? 1 : 0) +
    (selectedDatePreset?.label !== datePresets[0].label ? 1 : 0);

  return (
    <div className="bg-black border-[#E0E0E0]">
      {/* Mobile: collapsible filters toggle */}
      <button
        type="button"
        className="md:hidden w-full flex items-center justify-between px-4 py-3 text-white"
        onClick={() => setMobileFiltersOpen((prev) => !prev)}
      >
        <span className="flex items-center gap-2 text-sm font-semibold">
          Filters
          {activeFilterCount > 0 && (
            <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-[#FBB033] text-black text-[11px] font-bold flex items-center justify-center">
              {activeFilterCount}
            </span>
          )}
        </span>
        <span className="text-[#FBB033] text-xs">
          {mobileFiltersOpen ? "Hide ▲" : "Show ▼"}
        </span>
      </button>

      <div
        className={`${
          mobileFiltersOpen ? "flex" : "hidden"
        } md:flex flex-col md:flex-row md:flex-wrap gap-4 md:items-center p-4 pt-0 md:pt-4`}
      >
      {/* Employee multi-select */}
      <div className="flex flex-col text-white w-full md:w-auto md:min-w-[200px]">
        <label className="mb-1 text-sm">Employee</label>
        <Select
          isMulti
          menuPortalTarget={document.body}
          menuPosition="fixed"
          options={(data?.getEmployeelist?.employee || []).map((emp) => ({
            value: emp.id,
            label: emp.fullName,
          }))}
          value={(data?.getEmployeelist?.employee || [])
            .filter((emp) => selectedEmployeeIds.includes(emp.id))
            .map((emp) => ({ value: emp.id, label: emp.fullName }))}
          onChange={(selected) =>
            setSelectedEmployeeIds(selected ? selected.map((s) => s.value) : [])
          }
          placeholder="All Employees"
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            control: (base) => ({
              ...base,
              backgroundColor: "#0c0b0b",
              borderColor: "#4B5563",
              color: "white",
            }),
            menu: (base) => ({
              ...base,
              backgroundColor: "#1a1a1a",
              color: "white",
            }),
            option: (base, { isFocused }) => ({
              ...base,
              backgroundColor: isFocused ? "#2a2a2a" : "#1a1a1a",
              color: "white",
            }),
            multiValue: (base) => ({
              ...base,
              backgroundColor: "#FBB033",
            }),
            multiValueLabel: (base) => ({
              ...base,
              color: "black",
            }),
            input: (base) => ({ ...base, color: "white" }),
            placeholder: (base) => ({ ...base, color: "#9ca3af" }),
          }}
        />

      </div>


      {/* Status multi-select */}
      <div className="flex flex-col text-white w-full md:w-auto">
        <label className="mb-1 text-sm">Status</label>
        <div className="flex flex-wrap gap-2">
          {(isPreviousBooking
            ? previousBookingsStatusOptions
            : upcomingBookingStatusOptions
          ).map((status) => (
            <button
              key={status}
              type="button"
              onClick={() => handleStatusToggle(status)}
              className={`px-2 py-1 border rounded-full text-sm ${
                selectedStatuses.includes(status)
                  ? "bg-[#FBB033] text-black border-[#FBB033]"
                  : "border-[#FBB033] text-[#FBB033]"
              }`}
            >
              {status}
            </button>
          ))}
          <button
            type="button"
            onClick={handleClearAllStatuses}
            className="px-2 py-1 border border-red-400 rounded-full text-sm text-red-400"
          >
            Clear All
          </button>
        </div>
      </div>

      {/* Verification Status */}
      <div className="flex flex-col text-white w-full md:w-auto">
        <label className="mb-1 text-sm">Verification Status</label>
        <select
          className="px-2 py-1 bg-[#0c0b0b] border-b border-[#E0E0E0] outline-none w-full md:w-auto"
          value={selectedVerificationStatus ?? ""}
          onChange={(e) =>
            setSelectedVerificationStatus(
              e.target.value === "" ? null : e.target.value
            )
          }
        >
          {verificationStatusOptions.map((opt) => (
            <option key={String(opt.value ?? "")} value={opt.value ?? ""}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Date Preset */}
      <div className="flex flex-col text-white w-full md:w-auto">
        <label className="mb-1 text-sm">Date Range</label>
        <select
          className="px-2 py-1 bg-[#0c0b0b] border-b border-[#E0E0E0] outline-none w-full md:w-auto"
          value={selectedDatePreset?.label}
          onChange={(e) => {
            const preset = datePresets.find((p) => p.label === e.target.value);
            setSelectedDatePreset(preset);
          }}
        >
          {datePresets.map((preset) => (
            <option key={preset.label} value={preset.label}>
              {preset.label}
            </option>
          ))}
        </select>
      </div>

      {/* Custom date range */}
      {selectedDatePreset?.label === "Custom" && (
        <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-2 w-full md:w-auto">
          <div className="flex flex-col text-white w-full sm:w-auto">
            <label className="mb-1 text-sm">Start Date</label>
            <input
              type="date"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              className="px-2 py-1 bg-[#0c0b0b] border-b border-[#E0E0E0] outline-none text-white w-full sm:w-auto"
            />
          </div>
          <div className="flex flex-col text-white w-full sm:w-auto">
            <label className="mb-1 text-sm">End Date</label>
            <input
              type="date"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              className="px-2 py-1 bg-[#0c0b0b] border-b border-[#E0E0E0] outline-none text-white w-full sm:w-auto"
            />
          </div>
        </div>
      )}
      <div className="h-2"></div>
      {issuedReportedBookings > 0 && (
  <div
    className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200
      ${
        selectedIssueType === "REPORTED"
          ? "bg-red-600 text-white shadow-md"
          : "bg-red-600/15 text-red-500 border border-red-500/40 hover:bg-red-600/25"
      }`}
  >

    {/* Text */}
    <span
      onClick={handleToggleIssueType}
      className="cursor-pointer flex items-center gap-2"
    >
      {/* Warning Dot */}
      <span className={`h-2 w-2 rounded-full ${
        selectedIssueType === "REPORTED"
          ? "bg-white"
          : "bg-red-500"
      }`} />

      Open Issues ({issuedReportedBookings})
    </span>

    {/* Clear Button (Only when active) */}
    {selectedIssueType === "REPORTED" && (
      <>
        <div className="h-4 w-px bg-white/40" />

        <button
          onClick={(e) => {
            e.stopPropagation();
            handleToggleIssueType();
          }}
          className="hover:bg-white/20 rounded-full w-5 h-5 flex items-center justify-center text-xs transition"
        >
          ✕
        </button>
      </>
    )}
  </div>
)}

      {bookingsWithRemark > 0 && (
  <div
    className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200
      ${
        selectedHasRemark
          ? "bg-[#FBB033] text-black shadow-md"
          : "bg-[#FBB033]/15 text-[#FBB033] border border-[#FBB033]/40 hover:bg-[#FBB033]/25"
      }`}
  >
    <span
      onClick={handleToggleHasRemark}
      className="cursor-pointer flex items-center gap-2"
    >
      <span className={`h-2 w-2 rounded-full ${
        selectedHasRemark
          ? "bg-black"
          : "bg-[#FBB033]"
      }`} />

      Has Remarks ({bookingsWithRemark})
    </span>

    {selectedHasRemark && (
      <>
        <div className="h-4 w-px bg-black/30" />

        <button
          onClick={(e) => {
            e.stopPropagation();
            handleToggleHasRemark();
          }}
          className="hover:bg-black/20 rounded-full w-5 h-5 flex items-center justify-center text-xs transition"
        >
          ✕
        </button>
      </>
    )}
  </div>
)}

      {/* 🟡 UNACKNOWLEDGED FILTER */}
      {unacknowledgedCount > 0 && (
  <div
    className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200
      ${
        showUnacknowledged
          ? "bg-amber-400 text-black shadow-md"
          : "bg-amber-400/15 text-amber-400 border border-amber-400/40 hover:bg-amber-400/25"
      }`}
  >
    <span
      onClick={() => setShowUnacknowledged((prev) => !prev)}
      className="cursor-pointer flex items-center gap-2"
    >
      <span className={`h-2 w-2 rounded-full ${
        showUnacknowledged ? "bg-black" : "bg-amber-400"
      }`} />
      Unacknowledged ({unacknowledgedCount})
    </span>
    {showUnacknowledged && (
      <>
        <div className="h-4 w-px bg-black/30" />
        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowUnacknowledged(false);
          }}
          className="hover:bg-black/20 rounded-full w-5 h-5 flex items-center justify-center text-xs transition"
        >
          ✕
        </button>
      </>
    )}
  </div>
)}

      {/* 🧪 DUMMY EMPLOYEE FILTER */}
      {dummyEmployeeCount > 0 && (
  <div
    className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200
      ${
        showDummyEmployee
          ? "bg-purple-400 text-black shadow-md"
          : "bg-purple-400/15 text-purple-400 border border-purple-400/40 hover:bg-purple-400/25"
      }`}
  >
    <span
      onClick={() => setShowDummyEmployee((prev) => !prev)}
      className="cursor-pointer flex items-center gap-2"
    >
      <span className={`h-2 w-2 rounded-full ${
        showDummyEmployee ? "bg-black" : "bg-purple-400"
      }`} />
      Dummy Employee Bookings ({dummyEmployeeCount})
    </span>
    {showDummyEmployee && (
      <>
        <div className="h-4 w-px bg-black/30" />
        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowDummyEmployee(false);
          }}
          className="hover:bg-black/20 rounded-full w-5 h-5 flex items-center justify-center text-xs transition"
        >
          ✕
        </button>
      </>
    )}
  </div>
)}

      {/* 🚫 BLOCKED USER FILTER */}
      {blockedUserCount > 0 && (
  <div
    className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200
      ${
        showBlockedUser
          ? "bg-red-500 text-black shadow-md"
          : "bg-red-500/15 text-red-500 border border-red-500/40 hover:bg-red-500/25"
      }`}
  >
    <span
      onClick={() => setShowBlockedUser((prev) => !prev)}
      className="cursor-pointer flex items-center gap-2"
    >
      <span className={`h-2 w-2 rounded-full ${
        showBlockedUser ? "bg-black" : "bg-red-500"
      }`} />
      Blocked User Bookings ({blockedUserCount})
    </span>
    {showBlockedUser && (
      <>
        <div className="h-4 w-px bg-black/30" />
        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowBlockedUser(false);
          }}
          className="hover:bg-black/20 rounded-full w-5 h-5 flex items-center justify-center text-xs transition"
        >
          ✕
        </button>
      </>
    )}
  </div>
)}

      {/* ⏳ NO-SHOW AWAITING RESPONSE FILTER - bookings where an employee
          flagged "couldn't reach them" and the no-show clock is actively
          ticking (see no-show-hold-sweep.job.js) */}
      {noShowAwaitingCount > 0 && (
  <div
    className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200
      ${
        showNoShowAwaiting
          ? "bg-amber-500 text-black shadow-md"
          : "bg-amber-500/15 text-amber-500 border border-amber-500/40 hover:bg-amber-500/25"
      }`}
  >
    <span
      onClick={() => setShowNoShowAwaiting((prev) => !prev)}
      className="cursor-pointer flex items-center gap-2"
    >
      <span className={`h-2 w-2 rounded-full ${
        showNoShowAwaiting ? "bg-black" : "bg-amber-500"
      }`} />
      Awaiting No-Show Response ({noShowAwaitingCount})
    </span>
    {showNoShowAwaiting && (
      <>
        <div className="h-4 w-px bg-black/30" />
        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowNoShowAwaiting(false);
          }}
          className="hover:bg-black/20 rounded-full w-5 h-5 flex items-center justify-center text-xs transition"
        >
          ✕
        </button>
      </>
    )}
  </div>
)}

      {/* 🔄 Reset filters */}
      <div className="flex flex-col text-white w-full md:w-auto md:ml-auto">
        <label className="mb-1 text-sm hidden md:block">&nbsp;</label>
        <button
          type="button"
          onClick={handleResetFilters}
          className="px-3 py-1 border border-[#E0E0E0] rounded-full text-sm text-[#FBB033] w-full md:w-auto"
        >
          Reset Filters
        </button>
      </div>

      {/* Mobile: close panel */}
      <button
        type="button"
        className="md:hidden w-full px-3 py-2 rounded-md bg-[#FBB033] text-black text-sm font-semibold"
        onClick={() => setMobileFiltersOpen(false)}
      >
        Done
      </button>
      </div>
    </div>
  );
};

export default BookingFilterBar;
